import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';

import { Observable, Subject, Subscription, catchError, debounceTime, of, switchMap } from 'rxjs';

import { MentionsApi } from '../../../api/mentions/mentions.api';
import { MentionsSearchResponseModel } from '../../../api/mentions/model/mentions-search-response.model';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { MonitoringSourcePickerFieldWithoutFacetComponent }
    from '../../../Monitor/Shared/components/MonitoringSourcePickerField/monitoring-source-picker-field-without-facet.component';
import { MonitoringSeriesTypeEnum } from '../../../Monitor/Shared/enums/monitoring-series-type.enum';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { MonitoringSourcePickerFieldModel } from '../../../Monitor/Shared/models/monitoring-source-picker-field.model';
import { MonitoringBackEndDataSourceModel } from '../../../Monitor/Shared/models/monitoring-back-end-data-source.model';
import { CurationAgentSourceModel, quotaSampleDays } from '../../models/curation-agent-source.model';

/**
 * One "Source N" card in the create/edit curation agent panel: the monitoring source picker plus the
 * projected quota banner for whatever it resolves to. The source model is mutated in place, so the
 * panel sees selections and quota estimates without a change event.
 *
 * The without-facet picker is used deliberately - it provides its own MentionsNoFacetFilterService, so
 * each row filters independently. It does not run a search of its own, so this component counts the
 * sample window itself to drive the quota estimate.
 */
@Component({
    selector: 'agpr-curation-agent-source-row',
    templateUrl: './curation-agent-source-row.component.html',
    styleUrls: ['./curation-agent-source-row.component.scss'],
    standalone: false
})
export class CurationAgentSourceRowComponent implements OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly messageSeverity = messageSeverityEnum;

    @Input() id: string;
    @Input() source: CurationAgentSourceModel;
    /** 1 based, purely for the "Source N" heading */
    @Input() position: number = 1;
    @Input() canRemove: boolean = false;
    /** Overrides the "Source N" label, for callers that name the row themselves */
    @Input() heading: string;
    /** The monthly quota estimate. Off where the row is not part of a saved agent */
    @Input() showQuota: boolean = true;
    /** The agent the row belongs to, kept out of its own source list */
    @Input() agentId: number;

    @Output() removed: EventEmitter<CurationAgentSourceModel> = new EventEmitter<CurationAgentSourceModel>();
    /** The picked source changed, for callers that count or search off it */
    @Output() changed: EventEmitter<CurationAgentSourceModel> = new EventEmitter<CurationAgentSourceModel>();

    /**
     * The picker ships its own "view source" control anchored to itself, where it lands on top of the
     * filter button. It is hidden in favour of the row's context menu, which delegates back to it.
     */
    @ViewChild(MonitoringSourcePickerFieldWithoutFacetComponent)
    picker: MonitoringSourcePickerFieldWithoutFacetComponent;

    /**
     * Date range and result feedback are left out: the agent runs continuously, so there is no range to
     * pick, and the quota banner replaces the mention count message.
     */
    readonly field: MonitoringSourcePickerFieldModel = new MonitoringSourcePickerFieldModel(
        MonitoringSourcePickerFieldWithoutFacetComponent,
        'dataSource',
        null,
        null,
        {
            showFilters: true,
            showDateRange: false,
            showResultsFeedback: false,
            showLoadingFeedback: false,
            dataSourceOptions: [
                new DropdownOptionModel('Monitoring.source.type.topic', MonitoringSeriesTypeEnum.topic),
                new DropdownOptionModel('Reports.series.type.folder', MonitoringSeriesTypeEnum.folder),
                new DropdownOptionModel('Monitoring.source.type.tag', MonitoringSeriesTypeEnum.tag),
                new DropdownOptionModel('Monitoring.source.type.tagGroup', MonitoringSeriesTypeEnum.tagGroup),
                new DropdownOptionModel('Monitoring.source.type.subscription', MonitoringSeriesTypeEnum.subscription),
                new DropdownOptionModel('Monitoring.source.type.sourceGroup', MonitoringSeriesTypeEnum.sourceGroup),
                new DropdownOptionModel('Monitoring.source.type.socialTopic', MonitoringSeriesTypeEnum.socialTopic),
                new DropdownOptionModel('Monitoring.source.type.theme', MonitoringSeriesTypeEnum.theme),
                new DropdownOptionModel('Monitoring.source.type.aiAgent', MonitoringSeriesTypeEnum.aiAgent)
            ]
        }
    );

    countingQuota: boolean = false;

    // The picker re-emits on every filter keystroke, so coalesce before hitting the search endpoint
    private readonly quotaRequests: Subject<CurationAgentSourceModel> = new Subject<CurationAgentSourceModel>();
    /**
     * The search the last count was asked for. The picker settles in more than one emission when a
     * row opens on a saved source - it announces the source first and its filters once they load -
     * and only the emissions that change the search are worth another count.
     */
    private countedSearch: string = null;
    private readonly subscriptions: Subscription[] = [];

    constructor(private mentionsApi: MentionsApi) {
        this.subscriptions.push(
            this.quotaRequests.pipe(
                debounceTime(500),
                switchMap(source => this.countSampleMentions(source))
            ).subscribe(count => {
                this.source.sampleMentionCount = count;
                this.countingQuota = false;
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    onDataSourceChanged(dataSource: MonitoringBackEndDataSourceModel): void {
        this.source.dataSource = dataSource;
        this.changed.emit(this.source);

        if (!this.source.isComplete()) {
            this.source.sampleMentionCount = null;
            this.countedSearch = null;
            this.countingQuota = false;

            return;
        }

        if (!this.showQuota) {
            return;
        }

        const search = this.searchSignature(this.source);

        if (search === this.countedSearch) {
            return;
        }

        this.countedSearch = search;
        this.countingQuota = true;
        this.quotaRequests.next(this.source);
    }

    /** Opens the selected source's mentions in a new tab, using the picker's own resolution logic */
    viewSource(event: Event): void {
        this.picker?.viewSource(event);
    }

    remove(): void {
        this.removed.emit(this.source);
    }

    /** What the count would be asked for, so an emission that changes nothing can be passed over */
    private searchSignature(source: CurationAgentSourceModel): string {
        const criteria = this.searchCriteria(source);

        return criteria ? JSON.stringify(criteria.getApiEntity().query) : null;
    }

    private searchCriteria(source: CurationAgentSourceModel): MentionSearchCriteriaModel {
        const dataSource = source.dataSource;

        return MentionSearchCriteriaModel.createFiltered(
            dataSource?.sourceSearchCriteria,
            dataSource?.filterSearchCriteria
        );
    }

    /**
     * Mentions the source produced over the sample window. Returns null on failure so the banner falls
     * back to "N/A" rather than showing a number we cannot stand behind.
     */
    private countSampleMentions(source: CurationAgentSourceModel): Observable<number> {
        const dataSource = source.dataSource;
        const criteria = this.searchCriteria(source);

        if (!criteria) {
            return of(null);
        }

        criteria.query.dateQuery = DateQueryModel.getLastXDaysQuery(quotaSampleDays);
        // Only the total is needed, so ask for as little as the endpoint allows
        criteria.pageSize = 1;
        criteria.facets = [];
        criteria.dateFacets = [];

        const apiEntity = criteria.getApiEntity();
        const search = dataSource.isSavedSource()
            ? this.mentionsApi.searchAccount(apiEntity)
            : this.mentionsApi.searchMentions(apiEntity);

        return search.pipe(
            switchMap((response: MentionsSearchResponseModel) => of(response?.totalResults ?? 0)),
            catchError(() => of(null))
        );
    }
}
